import { type PostHog } from '@posthog/react';
import posthog from 'posthog-js';

const PAGEVIEW_EVENT = '$pageview';

export type PageviewLocation = {
  pathname: string;
  currentUrl: string;
};

type FlushOptions = { transport: 'sendBeacon' };

export type PageviewTracker = {
  startVisit: (location: PageviewLocation) => void;
  markRendered: (location: PageviewLocation) => void;
  flushPending: (options?: FlushOptions) => void;
  documentShown: () => void;
  reset: () => void;
};

type Visit = { key: string; location: PageviewLocation; sent: boolean };

// A trailing slash does not start a new visit, so /pr-526 and /pr-526/ are the same page.
const visitKey = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

/**
 * @description Creates the tracker that sends exactly one PostHog $pageview per visit, where a visit is
 * a stay on one pathname (the rule PostHog's history_change autocapture uses). The $pageview is sent
 * when the visit's page renders, or, when the page never renders (a redirect, a loader the user left),
 * when the next visit starts or the tab closes. It always carries the visit's own URL. Like PostHog's
 * own first $pageview, nothing is sent until the tab has been visible (background tabs, prerender).
 * @param {Pick<PostHog, 'capture'>} client - The PostHog instance that sends the $pageview.
 * @returns {PageviewTracker} The tracker.
 */
export const createPageviewTracker = (client: Pick<PostHog, 'capture'>): PageviewTracker => {
  let current: Visit | undefined;
  let shown = document.visibilityState === 'visible';
  let held: Visit[] = [];

  const capture = (visit: Visit, options?: FlushOptions) =>
    client.capture(
      PAGEVIEW_EVENT,
      { $current_url: visit.location.currentUrl, $pathname: visit.location.pathname },
      options,
    );

  const send = (visit: Visit, options?: FlushOptions) => {
    visit.sent = true;
    if (shown) capture(visit, options);
    else held.push(visit);
  };

  const startVisit = (location: PageviewLocation) => {
    const key = visitKey(location.pathname);
    if (current?.key === key) return;
    if (current && !current.sent) send(current);
    current = { key, location, sent: false };
  };

  return {
    startVisit,
    markRendered: (location) => {
      startVisit(location);
      if (current && !current.sent) send(current);
    },
    flushPending: (options) => {
      if (current && !current.sent) send(current, options);
    },
    documentShown: () => {
      if (shown) return;
      shown = true;
      held.forEach((visit) => capture(visit));
      held = [];
    },
    reset: () => {
      current = undefined;
      held = [];
      shown = document.visibilityState === 'visible';
    },
  };
};

export const pageviewTracker = createPageviewTracker(posthog);

/**
 * @description The location of the page the browser shows, used for the first visit at PostHog init.
 * @returns {PageviewLocation} The current window pathname and URL.
 */
export const windowPageviewLocation = (): PageviewLocation => ({
  pathname: window.location.pathname,
  currentUrl: window.location.href,
});

/**
 * @description Sends the pending $pageview when the tab closes or is hidden (mobile browsers often
 * discard a backgrounded tab without pagehide), and releases held pageviews when the tab is first shown.
 * Register it before posthog.init, so it runs before PostHog's own handler sends $pageleave and
 * flushes its queue.
 * @param {PageviewTracker} tracker - The tracker holding the current visit.
 */
export const registerPageviewLifecycleListeners = (tracker: PageviewTracker): void => {
  window.addEventListener('onpagehide' in self ? 'pagehide' : 'unload', () =>
    tracker.flushPending({ transport: 'sendBeacon' }),
  );
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tracker.documentShown();
    else tracker.flushPending({ transport: 'sendBeacon' });
  });
};
